import { z } from 'zod';
import type { MusicSyncReport } from './RoomMusic.js';

export const MUSIC_SYNC_MAX_AGE_MS = 65000;
export const MUSIC_SYNC_MAX_LISTENERS = 512;
export const musicSyncKey = (roomId: string) => `room:${roomId}:music:sync`;
export const musicSyncReportSchema = z.object({
  revision: z.number().int().nonnegative(), medianErrorMs: z.number().finite().min(0).max(60000),
  p95ErrorMs: z.number().finite().min(0).max(60000), outputLatencyKnown: z.boolean(),
  deviceClass: z.string().min(1).max(40), sampleCount: z.number().int().min(1).max(10000),
}).strict().refine(report => report.p95ErrorMs >= report.medianErrorMs);

export interface MusicSyncSummary {
  listeners: Array<MusicSyncReport & { listenerId: string; atMs: number; outOfBounds: boolean }>;
  pairwise: { medianMs: number; p95Ms: number; estimated: true } | null;
  serverTimeMs: number;
  truncated: boolean;
}

/** Shared by host and operator views. These marginal summaries estimate pairwise
 * error; they cannot establish acoustic percentiles or cover silent listeners. */
export function summarizeMusicSync(raw: Record<string, string>, now = Date.now()): MusicSyncSummary {
  const listeners: MusicSyncSummary['listeners'] = [];
  for (const [listenerId, value] of Object.entries(raw)) {
    if (value.length > 2048 || listenerId.length > 128) continue;
    try {
      const { atMs, ...report } = JSON.parse(value);
      if (!Number.isFinite(atMs) || atMs < now - MUSIC_SYNC_MAX_AGE_MS || atMs > now + 5000) continue;
      const parsed = musicSyncReportSchema.safeParse(report);
      if (!parsed.success) continue;
      listeners.push({ ...parsed.data, listenerId, atMs,
        outOfBounds: parsed.data.medianErrorMs > 20 || parsed.data.p95ErrorMs > 55 });
    } catch { /* Diagnostics must tolerate invalid or old stored reports. */ }
  }
  listeners.sort((a, b) => b.atMs - a.atMs || a.listenerId.localeCompare(b.listenerId));
  // Reaching the storage cap means older active listeners may have been evicted.
  const truncated = listeners.length >= MUSIC_SYNC_MAX_LISTENERS;
  listeners.length = Math.min(listeners.length, MUSIC_SYNC_MAX_LISTENERS);
  let medianMs = 0, p95Ms = 0;
  for (let i = 0; i < listeners.length; i++) for (let j = i + 1; j < listeners.length; j++) {
    medianMs = Math.max(medianMs, Math.hypot(listeners[i].medianErrorMs, listeners[j].medianErrorMs));
    p95Ms = Math.max(p95Ms, Math.hypot(listeners[i].p95ErrorMs, listeners[j].p95ErrorMs));
  }
  return { listeners, pairwise: listeners.length < 2 ? null : { medianMs, p95Ms, estimated: true }, serverTimeMs: now, truncated };
}
